import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Button, Stack,
    TablePagination,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import { toast } from "react-toastify";
import api, { apiError, tokens } from "../api";
import AdminHeader, { adminPage } from "../components/AdminHeader";
import AppFooter from "../components/AppFooter";
import LlmSettings from "../components/LlmSettings";

/*
 * Who has registered. Nothing else.
 *
 * Deliberately four columns and a way in. A plan does not belong here any more: one
 * account issues a token per plant and each token carries its own plan, so a single plan
 * against the account would be a number that is true of nobody. That question is answered
 * one level down, where the tokens are - and the conversations one level below that.
 *
 *   accounts → Details → that account's tokens → Chat history → the conversations
 *
 * Read-only: customers register themselves and issue their own keys, and a staff form
 * that writes a customer record is how one email ends up with two accounts.
 */

const th = {
    color: "rgba(255,255,255,.72)", fontSize: 12, fontWeight: 700,
    textTransform: "uppercase", letterSpacing: .6, whiteSpace: "nowrap",
    bgcolor: "rgba(255,255,255,.06)",
    borderBottom: "1px solid rgba(255,255,255,.18)",
};
const td = { color: "#fff", borderColor: "rgba(255,255,255,.07)" };

export default function AdminAccounts() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    // How many rows at a time. Server-side, so the choice has to travel with the request
    // rather than slicing a list the browser already holds.
    const [perPage, setPerPage] = useState(25);
    const [settings, setSettings] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/console/accounts", { params: { page: page + 1, limit: perPage } });
            setRows(data.data); setTotal(data.total);
        } catch (e) {
            if (e?.response?.status === 401) { tokens.console.clear(); navigate("/admin"); }
            else toast.error(apiError(e));
        }
    }, [page, perPage, navigate]);

    useEffect(() => { load(); }, [load]);

    return (
        <Box sx={{ ...adminPage, display: "flex", flexDirection: "column" }}>
            <AdminHeader />

            <Box sx={{ px: { xs: 2, md: 4 }, py: { xs: 3, md: 4 } }}>
                <Stack direction="row" spacing={2}
                    sx={{ alignItems: "center", justifyContent: "space-between", mb: 2.5 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: 24, letterSpacing: -0.5 }}>
                        Users Details
                    </Typography>
                    {/* The AI model, which belongs to nobody on this list - it is one switch
                        that moves the answers at every site at once. */}
                    <Button startIcon={<SettingsIcon />} onClick={() => setSettings(true)}
                        sx={{
                            borderRadius: 999, px: 2.5, color: "#9FE08C",
                            border: "1px solid rgba(123,217,95,.35)",
                            "&:hover": { bgcolor: "rgba(46,125,32,.18)" },
                        }}>
                        Settings
                    </Button>
                </Stack>

                <Box sx={{
                    borderRadius: 1.5, overflow: "hidden", overflowX: "auto",
                    bgcolor: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.12)",
                }}>
                    <Table size="small" sx={{ tableLayout: "fixed", minWidth: 760 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={th} align="center">Email</TableCell>
                                <TableCell sx={th} align="center">Name</TableCell>
                                <TableCell sx={th} align="center">Company</TableCell>
                                <TableCell sx={th} align="center">Registered</TableCell>
                                <TableCell sx={th} align="center">Details</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((r) => (
                                <TableRow key={r.id} hover>
                                    <TableCell sx={{ ...td, fontWeight: 600 }} align="center">{r.email}</TableCell>
                                    <TableCell sx={{ ...td, color: "rgba(255,255,255,.75)" }} align="center">
                                        {r.name || "—"}
                                    </TableCell>
                                    <TableCell sx={{ ...td, color: "rgba(255,255,255,.75)" }} align="center">
                                        {r.companyName || "—"}
                                    </TableCell>
                                    <TableCell sx={{ ...td, color: "rgba(255,255,255,.65)" }} align="center">
                                        {new Date(r.createdAt).toLocaleDateString("en-IN")}
                                    </TableCell>
                                    <TableCell sx={td} align="center">
                                        {/* The count rides on the button: an account with no
                                            tokens has nothing behind this click, and saying so
                                            here saves opening a page to find that out. */}
                                        <Button size="small"
                                            onClick={() => navigate(`/admin/accounts/${r.id}`)}
                                            sx={{
                                                borderRadius: 999, px: 2, color: "#9FE08C",
                                                border: "1px solid rgba(123,217,95,.35)",
                                                "&:hover": { bgcolor: "rgba(46,125,32,.18)" },
                                            }}>
                                            {r.tokenCount ? `Tokens (${r.tokenCount})` : "Open"}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {!rows.length && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center"
                                        sx={{ ...td, py: 5, color: "rgba(255,255,255,.5)" }}>
                                        No accounts found
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    {/* Always shown, even on one page: this is the only list in the
                        console that can run to hundreds, and the count tells whoever is
                        looking how many customers there are without counting rows. */}
                    <TablePagination component="div" count={total} page={page} rowsPerPage={perPage}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        onPageChange={(e, p) => setPage(p)}
                        // Back to the first page: page 4 of a 25-row list is nowhere in a
                        // 100-row one, and landing on an empty table looks like a failure.
                        onRowsPerPageChange={(e) => { setPerPage(Number(e.target.value)); setPage(0); }}
                        labelRowsPerPage="Rows per page"
                        // The dropdown opens in its own layer, outside this bar's sx, so
                        // without this it comes up white in the middle of a dark page.
                        slotProps={{
                            select: {
                                MenuProps: {
                                    slotProps: {
                                        paper: {
                                            sx: {
                                                bgcolor: "#0C1A12", color: "#fff",
                                                border: "1px solid rgba(255,255,255,.12)",
                                            },
                                        },
                                    },
                                },
                            },
                        }}
                        sx={{
                            color: "rgba(255,255,255,.7)",
                            borderTop: "1px solid rgba(255,255,255,.08)",
                            "& .MuiSvgIcon-root": { color: "rgba(255,255,255,.6)" },
                        }} />
                </Box>
            </Box>

            <AppFooter />
            <LlmSettings open={settings} onClose={() => setSettings(false)} />
        </Box>
    );
}
